import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
import { checkAndStoreRegcode } from '@/utils/regcode'
import { recoverFromChunkLoadError } from '@/utils/appVersion'
import { maybeRedirectMobileToH5 } from '@/utils/h5DeviceRedirect'
import { shouldUsePcWebsite } from '@/utils/deviceDetect'
import Login from '@/views/Login.vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      title: '登录',
      requiresAuth: false
    }
  },
  // 宣传/引导首页（独立全屏页，不套 BasicLayout；跳转地址见 landingConfig.js）
  {
    path: '/home',
    name: 'LandingHome',
    component: () => import('@/views/landing/index.vue'),
    meta: {
      title: '首页',
      requiresAuth: false
    }
  },
  {
    path: '/',
    component: () => import('@/layouts/BasicLayout.vue'),
    redirect: '/dashboard',
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: {
          title: '首页',
          requiresAuth: true
        }
      },
      // 英语学习模块
      {
        path: 'englishLearning/batchEditing',
        name: 'BatchEditing',
        component: () => import('@/views/englishLearning/batchEditing/index.vue'),
        meta: {
          title: '课程广场',
          requiresAuth: false, // 允许未登录访问
          keepAlive: true,
          mainScrollLocked: true
        }
      },
      {
        path: 'englishLearning/courseDetail',
        name: 'CourseDetail',
        component: () => import('@/views/englishLearning/courseDetail/index.vue'),
        meta: {
          title: '课程详情',
          requiresAuth: false, // 允许未登录访问
          mainScrollLocked: true // 与课程列表一致：由页面内 courseScrollWrap 滚动
        }
      },
      {
        path: 'englishLearning/distribution',
        name: 'Distribution',
        component: () => import('@/views/englishLearning/distribution/index.vue'),
        meta: {
          title: '分销推广',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/learningRecord',
        name: 'LearningRecord',
        component: () => import('@/views/englishLearning/learningRecord/index.vue'),
        meta: {
          title: '学习记录',
          requiresAuth: true
        }
      },
      {
        path: 'englishLearning/myCourse',
        name: 'MyCourse',
        component: () => import('@/views/englishLearning/myCourse/index.vue'),
        meta: {
          title: '我的课程',
          requiresAuth: true,
          keepAlive: true
        }
      },
      {
        path: 'englishLearning/wordLookup',
        name: 'WordLookup',
        component: () => import('@/views/englishLearning/wordLookup/index.vue'),
        meta: {
          title: '查单词',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/writePaper',
        name: 'WritePaper',
        component: () => import('@/views/englishLearning/writePaper/index.vue'),
        meta: {
          title: '英文字帖',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/speakingPlaza',
        name: 'SpeakingPlaza',
        component: () => import('@/views/englishLearning/speakingPlaza/index.vue'),
        meta: {
          title: '口语广场',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/speakingPlaza/chat',
        name: 'SpeakingPlazaChat',
        component: () => import('@/views/englishLearning/speakingPlaza/chat.vue'),
        meta: {
          title: '口语广场',
          requiresAuth: true,
          mainPadding: 'p-0',
          mainScrollLocked: true
        }
      },
      {
        path: 'englishLearning/phonics',
        name: 'PhonicsIndex',
        component: () => import('@/views/englishLearning/phonics/index.vue'),
        meta: {
          title: '自然拼读',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/phonics/:chapterId',
        name: 'PhonicsLesson',
        component: () => import('@/views/englishLearning/phonics/lesson.vue'),
        meta: {
          title: '自然拼读',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/writePaper/alphabet',
        name: 'WritePaperAlphabet',
        component: () => import('@/views/englishLearning/writePaper/generate/index.vue'),
        meta: {
          title: '英文字母字帖',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/writePaper/wordTrace',
        name: 'WritePaperWordTrace',
        component: () => import('@/views/englishLearning/writePaper/generate/index.vue'),
        meta: {
          title: '英文单词描写帖',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/writePaper/wordHalfTrace',
        name: 'WritePaperWordHalfTrace',
        component: () => import('@/views/englishLearning/writePaper/generate/index.vue'),
        meta: {
          title: '英文半描半练字帖',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/writePaper/articleUpperHalf',
        name: 'WritePaperArticleUpperHalf',
        component: () => import('@/views/englishLearning/writePaper/generate/index.vue'),
        meta: {
          title: '英文上半截文章帖',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/writePaper/word',
        name: 'WritePaperWord',
        component: () => import('@/views/englishLearning/writePaper/generate/index.vue'),
        meta: {
          title: '英文单词字帖',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/writePaper/article',
        name: 'WritePaperArticle',
        component: () => import('@/views/englishLearning/writePaper/generate/index.vue'),
        meta: {
          title: '英文文章帖',
          requiresAuth: true,
          mainPadding: 'p-0'
        }
      },
      {
        path: 'englishLearning/rankingList',
        name: 'RankingList',
        component: () => import('@/views/englishLearning/rankingList/index.vue'),
        meta: {
          title: '排行榜',
          requiresAuth: true
        }
      },
      {
        path: 'englishLearning/vip',
        name: 'Vip',
        component: () => import('@/views/englishLearning/vip/index.vue'),
        meta: {
          title: 'VIP会员',
          requiresAuth: true
        }
      },
      {
        path: 'englishLearning/aICreationWorkshop',
        name: 'AICreationWorkshop',
        component: () => import('@/views/englishLearning/aICreationWorkshop/index.vue'),
        meta: {
          title: 'AI创课',
          requiresAuth: true
        }
      },
      {
        path: 'englishLearning/honorPartner',
        name: 'HonorPartner',
        component: () => import('@/views/englishLearning/honorPartner/index.vue'),
        meta: {
          title: '合伙人',
          requiresAuth: false, // 允许未登录访问
          mainPadding: 'p-0'
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: '404',
      requiresAuth: false
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

/**
 * 旧邀请外链（仅 PC/平板）：`/?regcode=`、`/dashboard?regcode=` → `/home?regcode=`
 * 手机仍由 maybeRedirectMobileToH5 进 H5，不在此处理
 */
function shouldRedirectOldInviteRootToHome (to) {
  if (!shouldUsePcWebsite()) return false
  const path = to.path || '/'
  if (path !== '/' && path !== '/dashboard') return false
  const code = to.query?.regcode
  return !!(code && String(code).trim())
}

// 路由守卫
router.beforeEach((to, from, next) => {
  if (maybeRedirectMobileToH5()) return

  if (shouldRedirectOldInviteRootToHome(to)) {
    next({
      path: '/home',
      query: { ...to.query },
      hash: to.hash,
      replace: true
    })
    return
  }

  // 每次路由切换时检查 URL 中是否有 regcode，有则持久化到 localStorage
  // 这样用户通过 ?regcode=xxx 进入任意页后被重定向到 /home 或登录页，regcode 也不会丢失
  checkAndStoreRegcode(to)

  // 设置页面标题
  if (to.meta.title) {
    const conf = store.getters['common/systemConf'] || {}
    const siteTitle = conf.web_site_title || '词呗AI'
    const slogan = conf.web_site_slogan || ''
    document.title = slogan
      ? `${siteTitle} - ${slogan} - ${to.meta.title}`
      : `${siteTitle} - ${to.meta.title}`
  }

  // 检查是否需要认证
  if (to.meta.requiresAuth) {
    const token = store.getters['user/token']
    if (token) {
      // 如果有 token，检查用户信息是否存在
      if (!store.getters['user/userInfo']) {
        // 如果没有用户信息，尝试获取
        store.dispatch('user/getUserInfo').then(() => {
          next()
        }).catch(() => {
          // 获取失败，跳转到登录页
          next({
            path: '/login',
            query: { redirect: to.fullPath }
          })
        })
      } else {
        next()
      }
    } else {
      // 没有 token，跳转到宣传首页（登录过期 401 仍由 request 拦截器跳 /login）
      next({ path: '/home' })
    }
  } else {
    // 不需要认证的页面
    const token = store.getters['user/token']
    if (to.path === '/login' && token) {
      // 已登录用户访问登录页，重定向到首页
      next('/')
    } else {
      next()
    }
  }
})

// 发版后旧 chunk 缓存失效时，懒加载可能 404；带 version.json 版本号刷新一次恢复
let isRecoveringChunkError = false
router.onError((error) => {
  const message = error?.message || ''
  const isChunkLoadError = error?.name === 'ChunkLoadError'
    || /Loading chunk .* failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
  if (isChunkLoadError && !isRecoveringChunkError) {
    isRecoveringChunkError = true
    void recoverFromChunkLoadError(error)
  }
})

export default router
